import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { apiGet, formatLKR, formatPct, formatNumber, formatDate as formatShortDate } from "@/lib/api";
import KpiTile from "@/components/dashboard/KpiTile";
import SignalBadge from "@/components/common/SignalBadge";
import LoadingState from "@/components/common/LoadingState";
import ErrorState from "@/components/common/ErrorState";
import { CompanyLink, SymbolLink } from "@/components/common/StockLinks";
import WatchlistStarButton from "@/components/common/WatchlistStarButton";
import WelcomeBanner from "@/components/onboarding/WelcomeBanner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Activity, ArrowUpRight, ArrowDownRight, Coins, BarChart3, Sparkles, ChevronRight
} from "lucide-react";

const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    apiGet("/market/overview")
      .then((d) => setData(d))
      .catch((e) => setError(e.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const onRefresh = () => load();
    window.addEventListener("cse:data-refreshed", onRefresh);
    return () => window.removeEventListener("cse:data-refreshed", onRefresh);
  }, [load]);

  if (loading) return <LoadingState label="Loading market overview…" />;
  if (error) return <ErrorState description={error} onRetry={load} />;
  if (!data) return null;

  const k = data.kpis;

  return (
    <div className="space-y-6 md:space-y-8" data-testid="dashboard-page">
      {/* Test 9 Fix 6 — dismissible welcome banner replaces the previous
          modal onboarding tour. Hidden for admins, paid users, and once
          the user clicks Dismiss (preference saved in localStorage). */}
      <WelcomeBanner />

      <section
        data-testid="dashboard-kpi-grid"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3"
      >
        <KpiTile
          label="Securities tracked"
          value={formatNumber(k.total_securities)}
          helpText={`${k.high_confidence_count ?? k.full_data_count ?? 0} with full data`}
          icon={BarChart3}
          testId="kpi-securities"
        />
        <KpiTile
          label="BUY signals"
          value={formatNumber(k.buy_signals ?? 0)}
          helpText="composite score ≥ 65"
          icon={ArrowUpRight}
          accent
          testId="kpi-buy-signals"
          linkTo="/stocks?signal=BUY"
        />
        <KpiTile
          label="WATCH signals"
          value={formatNumber(k.watch_signals ?? 0)}
          helpText="score 40 – 64"
          icon={Activity}
          testId="kpi-watch-signals"
          linkTo="/stocks?signal=WATCH"
        />
        <KpiTile
          label="Advancers / Decliners"
          value={`${k.advancers} / ${k.decliners}`}
          helpText={`${k.unchanged} unchanged`}
          icon={Activity}
          testId="kpi-breadth"
          linkTo="/stocks?sort=change_desc"
        />
        <KpiTile
          label="Median yield"
          value={`${k.median_yield_pct.toFixed(2)}%`}
          icon={Coins}
          testId="kpi-yield"
          linkTo="/stocks?sort=yield_desc"
        />
        <KpiTile
          label="Total market cap"
          value={formatLKR(k.total_market_cap, { compact: true, decimals: 1 })}
          icon={Sparkles}
          testId="kpi-mcap"
        />
      </section>

      <section className="grid grid-cols-1 xl:grid-cols-[1.55fr_1fr] gap-6">
        <Card className="border bg-card">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="font-display">Top movers (today)</CardTitle>
                <p className="text-xs text-muted-foreground mt-0.5">Largest absolute change since previous close</p>
              </div>
              <Link to="/stocks" className="text-sm text-primary hover:underline">
                See all stocks <ChevronRight className="inline h-3 w-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Gainers</div>
              <MoverList items={data.top_gainers} testId="top-gainers" tone="up" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Losers</div>
              <MoverList items={data.top_losers} testId="top-losers" tone="down" />
            </div>
          </CardContent>
        </Card>

        <Card className="border bg-card" data-testid="dashboard-best-value-spotlight">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-display">Best Value Spotlight</CardTitle>
              <Link to="/best-value" className="text-sm text-primary hover:underline">
                Open finder <ChevronRight className="inline h-3 w-3" />
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">Non-voting shares at the largest yield uplift today</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {data.best_value.length === 0 && (
              <div className="text-sm text-muted-foreground">No dual-share data available.</div>
            )}
            {data.best_value.map((b) => (
              <Link
                key={b.company_code}
                to={`/stocks/${b.company_code}`}
                state={{ from: { pathname: "/dashboard", label: "Dashboard" } }}
                className="block rounded-lg border p-3 bg-background hover:bg-accent/40 transition-colors duration-150"
                data-testid={`best-value-spotlight-${b.company_code}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{b.company_name}</div>
                    <div className="text-xs text-muted-foreground font-mono truncate">
                      {b.voting_symbol} · {b.non_voting_symbol}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Yield gap</div>
                    <div className="text-base font-semibold tabular text-[hsl(var(--success))]">
                      {formatPct(b.yield_difference_pct)}
                    </div>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-muted-foreground">
                  <span>Discount <span className="text-foreground font-semibold tabular">{formatPct(-Math.abs(b.price_discount_pct))}</span></span>
                  <span className="text-right">NV yield <span className="text-foreground font-semibold tabular">{formatPct(b.non_voting_yield)}</span></span>
                </div>
                {(b.voting_score != null || b.non_voting_score != null) && (
                  <div className="mt-2 flex items-center justify-between gap-2 pt-2 border-t border-border/60 text-[11px]">
                    <div className="flex items-center gap-1.5" data-testid={`best-value-spotlight-${b.company_code}-voting`}>
                      <span className="text-muted-foreground">Voting</span>
                      <span className="font-display font-semibold tabular text-foreground">{b.voting_score ?? "—"}</span>
                      {b.voting_signal && (
                        <SignalBadge signal={b.voting_signal} className="text-[9px] px-1 py-0" />
                      )}
                    </div>
                    <span className="text-muted-foreground/60">|</span>
                    <div className="flex items-center gap-1.5" data-testid={`best-value-spotlight-${b.company_code}-nv`}>
                      <span className="text-muted-foreground">NV</span>
                      <span className="font-display font-semibold tabular text-foreground">{b.non_voting_score ?? "—"}</span>
                      {b.non_voting_signal && (
                        <SignalBadge signal={b.non_voting_signal} className="text-[9px] px-1 py-0" />
                      )}
                    </div>
                  </div>
                )}
              </Link>
            ))}
          </CardContent>
        </Card>
      </section>

      {data.top_buys && data.top_buys.length > 0 && (
        <section>
          <Card className="border bg-card" data-testid="top-buys-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display">Top Buy Opportunities</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Highest composite scores across all {k.total_securities} securities — real TradingView fundamentals + technical signals
                  </p>
                </div>
                <Link to="/stocks" className="text-sm text-primary hover:underline">
                  See all <ChevronRight className="inline h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {data.top_buys.map((tb) => (
                  <Link
                    key={tb.cse_symbol}
                    to={`/stocks/${tb.company_code}`}
                    state={{ from: { pathname: "/dashboard", label: "Dashboard" } }}
                    className="block rounded-lg border p-3 bg-background hover:bg-accent/40 transition-colors duration-150"
                    data-testid={`top-buy-${tb.cse_symbol}`}
                  >
                    <div className="text-sm font-medium truncate">{tb.company_name}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{tb.cse_symbol}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-2xl font-display font-semibold tabular text-[hsl(var(--success))]">
                        {tb.composite_score}
                      </span>
                      <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 text-[10px]">
                        BUY
                      </Badge>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1">{tb.sector || "—"}</div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {data.top_yields && data.top_yields.length > 0 && (
        <section>
          <Card className="border bg-card" data-testid="top-yields-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-display">Top Dividend Yields</CardTitle>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Highest dividend-yielding stocks on CSE based on current TradingView data, across {k.total_securities} securities. Yields above 30% are excluded as likely data anomalies.
                  </p>
                </div>
                <Link to="/dividends" className="text-sm text-primary hover:underline">
                  Open dividends <ChevronRight className="inline h-3 w-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {data.top_yields.map((ty) => (
                  <Link
                    key={ty.cse_symbol}
                    to={`/stocks/${ty.company_code}`}
                    state={{ from: { pathname: "/dashboard", label: "Dashboard" } }}
                    className="block rounded-lg border p-3 bg-background hover:bg-accent/40 transition-colors duration-150"
                    data-testid={`top-yield-${ty.cse_symbol}`}
                  >
                    <div className="text-sm font-medium truncate">{ty.company_name}</div>
                    <div className="text-[11px] font-mono text-muted-foreground">{ty.cse_symbol}</div>
                    <div className="mt-2 flex items-baseline justify-between">
                      <span className="text-2xl font-display font-semibold tabular text-[hsl(var(--success))]">
                        {formatPct(ty.dividend_yield)}
                      </span>
                      <span className="text-[11px] text-muted-foreground tabular">
                        DPS{" "}
                        {ty.dividend_per_share
                          ? formatLKR(ty.dividend_per_share)
                          : ty.estimated_dps
                            ? `${formatLKR(ty.estimated_dps)} (est.)`
                            : "—"}
                      </span>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center justify-between">
                      <span>{ty.sector || "—"}</span>
                      {ty.payout_ratio != null && (
                        <span className="tabular">payout {ty.payout_ratio.toFixed(0)}%</span>
                      )}
                    </div>
                    {/* Issue #2 — XD / Pay dates for dividend investors */}
                    <div className="mt-2 pt-2 border-t border-border/60 space-y-0.5 text-[10px]">
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">XD date</span>
                        <span
                          className={`tabular font-medium ${
                            ty.xd_is_upcoming
                              ? "text-[hsl(var(--success))]"
                              : ty.xd_date
                                ? "text-foreground/80"
                                : "text-muted-foreground"
                          }`}
                          data-testid={`top-yield-xd-${ty.cse_symbol}`}
                        >
                          {ty.xd_date ? formatShortDate(ty.xd_date) : "— not announced"}
                          {ty.xd_is_upcoming && " • upcoming"}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground">Pay date</span>
                        <span
                          className="tabular text-foreground/80"
                          data-testid={`top-yield-pay-${ty.cse_symbol}`}
                        >
                          {ty.payment_date ? formatShortDate(ty.payment_date) : "—"}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <section>
        <Card className="border bg-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-display">Recent AI signals</CardTitle>
              <Link to="/ai-insights" className="text-sm text-primary hover:underline">
                Open AI Insights <ChevronRight className="inline h-3 w-3" />
              </Link>
            </div>
            <p className="text-xs text-muted-foreground">Per share-type signals from latest Claude analysis</p>
          </CardHeader>
          <CardContent>
            {data.recent_signals.length === 0 ? (
              <div className="text-sm text-muted-foreground">AI analysis is being generated. Refresh in a moment.</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.recent_signals.slice(0, 9).map((s, idx) => (
                  <motion.div
                    key={`${s.cse_symbol}-${idx}`}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: idx * 0.02 }}
                  >
                    <Link
                      to={`/stocks/${s.company_code}`}
                      state={{ from: { pathname: "/dashboard", label: "Dashboard" } }}
                      className="block rounded-lg border p-3 bg-background hover:bg-accent/40 transition-colors duration-150"
                      data-testid={`recent-signal-${s.cse_symbol}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{s.company_name}</div>
                          <div className="text-xs font-mono text-muted-foreground">{s.cse_symbol}</div>
                        </div>
                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <WatchlistStarButton
                            cseSymbol={s.cse_symbol}
                            companyName={s.company_name}
                          />
                          <SignalBadge signal={s.signal} />
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs">
                        <Badge variant="secondary" className="capitalize">{(s.share_type || "").replace("_", "-")}</Badge>
                        <div className="tabular text-muted-foreground">Score <span className="text-foreground font-medium">{s.score}</span></div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
};

const MoverList = ({ items, testId, tone }) => {
  if (!items || items.length === 0)
    return <div className="text-sm text-muted-foreground">No movers yet.</div>;
  const Arrow = tone === "up" ? ArrowUpRight : ArrowDownRight;
  const cls = tone === "up" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--destructive))]";
  return (
    <ul data-testid={testId} className="divide-y divide-border rounded-lg border bg-background overflow-hidden">
      {items.slice(0, 5).map((it) => (
        <li key={it.cse_symbol} className="px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-muted/40">
          <div className="min-w-0">
            <CompanyLink
              companyCode={it.company_code}
              name={it.company_name}
              fromLabel="Dashboard"
              className="text-sm font-medium truncate block"
              testId={`mover-name-${it.cse_symbol}`}
            />
            <SymbolLink
              companyCode={it.company_code}
              symbol={it.cse_symbol}
              fromLabel="Dashboard"
              className="text-muted-foreground"
              testId={`mover-symbol-${it.cse_symbol}`}
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="text-sm tabular">{formatLKR(it.close_price)}</div>
            <div className={`flex items-center gap-1 text-sm tabular ${cls}`}>
              <Arrow className="h-3 w-3" />
              {formatPct(it.change_pct)}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
};

export default DashboardPage;
